import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";
import ImageUpload from "../components/ImageUpload";
import RichTextEditor from "../components/RichTextEditor";
import RichTextRenderer from "../components/RichTextRenderer";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Edit3,
  Flag,
  Hash,
  Info,
  MessageCircle,
  Plus,
  Send,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";

const EMPTY_CHANNEL_FORM = {
  name: "",
  description: "",
  about: "",
  banner_image: "",
  logo_image: "",
};

const tabs = [
  { key: "discussions", label: "Discussions" },
  { key: "members", label: "Membres" },
  { key: "about", label: "À propos" },
  { key: "reports", label: "Signalements" },
];


function CommunityAvatar({ picture, picturePosition = "center center", name, className = "" }) {
  const initial = (name || "?").slice(0, 1).toUpperCase();
  if (picture) {
    return (
      <img
        src={fileUrl(picture)}
        alt={name || "Membre"}
        className={`avatar object-cover ${className}`}
        style={{ objectPosition: picturePosition || "center center" }}
        loading="lazy"
      />
    );
  }
  return <div className={`avatar ${className}`}>{initial}</div>;
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="font-heading text-2xl font-black">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ChannelForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState({ ...EMPTY_CHANNEL_FORM, ...(initial || {}) });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4">
      <div>
        <label className="label mb-1 block">Nom de l'espace</label>
        <input className="input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ex : Discussion Générale" required />
      </div>

      <div>
        <label className="label mb-1 block">Description courte</label>
        <input className="input" value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Une phrase visible sous le titre" />
      </div>

      <ImageUpload label="Image bannière" type="image" storage="dataUrl" value={form.banner_image} onChange={(value) => update("banner_image", value)} />
      <ImageUpload label="Image / logo de l'espace" type="image" storage="dataUrl" value={form.logo_image} onChange={(value) => update("logo_image", value)} />

      <RichTextEditor label="À propos de l'espace" value={form.about} onChange={(value) => update("about", value)} minHeight={220} placeholder="Présente l’objectif de cet espace, les règles, l’ambiance attendue…" />

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Annuler</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? "Enregistrement..." : "Enregistrer"}</button>
      </div>
    </form>
  );
}

function ReportModal({ target, channelId, onClose, onDone }) {
  const [reason, setReason] = useState("Manque de respect");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/community/reports", {
        target_type: target.type,
        target_id: target.id,
        channel_id: channelId,
        reason,
        details,
      });
      onDone?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’envoyer le signalement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Signaler un contenu" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div>
          <label className="label mb-1 block">Motif</label>
          <select className="input" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option>Manque de respect</option>
            <option>Spam / publicité</option>
            <option>Contenu inapproprié</option>
            <option>Harcèlement</option>
            <option>Autre</option>
          </select>
        </div>
        <div>
          <label className="label mb-1 block">Détails optionnels</label>
          <textarea className="input" rows={4} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Ajoute du contexte pour aider la modération..." />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? "Envoi..." : "Envoyer le signalement"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ChannelHeader({ channel, threadCount, isAdmin, onInvite, onEdit, onDelete }) {
  const banner = channel?.banner_image ? fileUrl(channel.banner_image) : "";
  const logo = channel?.logo_image ? fileUrl(channel.logo_image) : "";

  return (
    <div className="card overflow-hidden mb-8">
      <div className="h-44 md:h-56 bg-gradient-to-br from-emerald-100 via-brand-mint to-amber-100 relative">
        {banner && <img src={banner} alt="Bannière" className="absolute inset-0 h-full w-full object-cover" />}
      </div>
      <div className="p-5 md:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-7">
        <div className="h-24 w-24 rounded-2xl bg-white shadow-elev border border-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {logo ? <img src={logo} alt={channel.name} className="h-full w-full object-cover" /> : <Hash className="h-10 w-10 text-brand-green" />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-3xl font-black">{channel.name}</h2>
          <p className="text-gray-600 mt-1">{channel.description}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
            <span>{channel.member_count || 0} membre{(channel.member_count || 0) > 1 ? "s" : ""}</span>
            <span>{threadCount} discussion{threadCount > 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onInvite} className="btn-primary bg-black hover:bg-gray-900">
            <UserPlus className="h-4 w-4" /> Inviter
          </button>
          {isAdmin && (
            <>
              <button type="button" onClick={onEdit} className="btn-ghost !px-4" title="Modifier l'espace">
                <Edit3 className="h-5 w-5" />
              </button>
              <button type="button" onClick={onDelete} className="btn-ghost !px-4 !border-red-200 !text-red-600 hover:!bg-red-50" title="Supprimer l'espace">
                <Trash2 className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Community() {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [threads, setThreads] = useState([]);
  const [members, setMembers] = useState([]);
  const [reports, setReports] = useState([]);
  const [tab, setTab] = useState("discussions");
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [channelModal, setChannelModal] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [savingChannel, setSavingChannel] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const activeThreadCount = threads.length;
  const visibleTabs = useMemo(() => tabs.filter((t) => t.key !== "reports" || isAdmin), [isAdmin]);

  const loadChannels = async (keepId) => {
    const r = await api.get("/community/channels");
    setChannels(r.data);
    if (!r.data.length) {
      setActive(null);
      return;
    }
    const next = r.data.find((c) => c.channel_id === keepId) || r.data.find((c) => c.channel_id === active?.channel_id) || r.data[0];
    setActive(next);
  };

  useEffect(() => { loadChannels(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) return;
    api.get(`/community/channels/${active.channel_id}/threads`).then((r) => setThreads(r.data));
    setMembers([]);
    setReports([]);
  }, [active?.channel_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active || tab !== "members" || !user) return;
    api.get(`/community/channels/${active.channel_id}/members`).then((r) => setMembers(r.data));
  }, [active?.channel_id, tab, user]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active || tab !== "reports" || !isAdmin) return;
    api.get(`/community/reports/admin?channel_id=${active.channel_id}`).then((r) => setReports(r.data));
  }, [active?.channel_id, tab, isAdmin]);  // eslint-disable-line react-hooks/exhaustive-deps

  const createThread = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    const { data } = await api.post("/community/threads", { channel_id: active.channel_id, title, body });
    setThreads((t) => [data, ...t]);
    setTitle(""); setBody(""); setCreating(false);
  };

  const saveChannel = async (form) => {
    setSavingChannel(true);
    setError("");
    try {
      const payload = { ...form, about: form.about || form.description || "" };
      if (channelModal?.mode === "edit") {
        const { data } = await api.patch(`/community/channels/${channelModal.channel.channel_id}`, payload);
        setChannels((prev) => prev.map((c) => c.channel_id === data.channel_id ? data : c));
        setActive(data);
      } else {
        const { data } = await api.post("/community/channels", payload);
        await loadChannels(data.channel_id);
        setTab("discussions");
      }
      setChannelModal(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’enregistrer cet espace.");
    } finally {
      setSavingChannel(false);
    }
  };

  const deleteChannel = async () => {
    if (!active) return;
    const ok = window.confirm(`Supprimer l'espace "${active.name}" ?\n\nToutes les discussions et réponses associées seront supprimées.`);
    if (!ok) return;
    await api.delete(`/community/channels/${active.channel_id}`);
    await loadChannels();
    window.location.reload();
  };

  const deleteThread = async (thread) => {
    if (!thread) return;
    const canDelete = isAdmin || thread.user_id === user?.user_id;
    if (!canDelete) return;

    const ok = window.confirm(`Supprimer la discussion "${thread.title}" ?\n\nToutes les réponses et les signalements associés seront supprimés.`);
    if (!ok) return;

    await api.delete(`/community/threads/${thread.thread_id}`);
    setThreads((prev) => prev.filter((item) => item.thread_id !== thread.thread_id));
    await loadChannels(active?.channel_id);
  };

  const invite = async () => {
    const url = `${window.location.origin}/community`;
    try {
      await navigator.clipboard.writeText(url);
      alert("Lien de la communauté copié.");
    } catch {
      alert(url);
    }
  };

  const resolveReport = async (reportId) => {
    await api.patch(`/community/reports/admin/${reportId}`, { status: "resolved" });
    setReports((prev) => prev.map((r) => r.report_id === reportId ? { ...r, status: "resolved" } : r));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="community-page">
      <div className="mb-10">
        <p className="label">Communauté SF Learning</p>
        <h1 className="font-heading text-4xl font-black mt-1">Échange avec les autres dirigeants</h1>
        <p className="text-gray-600 mt-1">Pose tes questions, partage tes victoires et soutiens les autres.</p>
      </div>

      {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="card p-3 self-start">
          <div className="flex items-center justify-between px-3 py-2">
            <p className="label">Espaces</p>
            {isAdmin && (
              <button type="button" onClick={() => setChannelModal({ mode: "create" })} className="text-brand-green hover:text-brand-orange" title="Créer un espace">
                <Plus className="h-5 w-5" />
              </button>
            )}
          </div>
          {channels.map((c) => (
            <button key={c.channel_id} onClick={() => { setActive(c); setTab("discussions"); }} className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${active?.channel_id === c.channel_id ? "bg-brand-green text-white font-semibold" : "hover:bg-emerald-50"}`} data-testid={`channel-btn-${c.channel_id}`}>
              <Hash className="w-4 h-4" /> {c.name}
            </button>
          ))}
          {isAdmin && (
            <button type="button" onClick={() => setChannelModal({ mode: "create" })} className="btn-primary w-full mt-4">
              <Plus className="h-4 w-4" /> Créer un espace
            </button>
          )}
        </aside>

        <main>
          {active && (
            <div>
              <ChannelHeader
                channel={active}
                threadCount={activeThreadCount}
                isAdmin={isAdmin}
                onInvite={invite}
                onEdit={() => setChannelModal({ mode: "edit", channel: active })}
                onDelete={deleteChannel}
              />

              <div className="mb-8 border-b border-emerald-100 flex gap-6 overflow-x-auto">
                {visibleTabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`whitespace-nowrap px-1 pb-4 text-sm md:text-base border-b-2 -mb-px ${tab === item.key ? "border-brand-green text-brand-green font-semibold" : "border-transparent text-gray-600 hover:text-brand-green"}`}
                  >
                    {item.label}
                    {item.key === "discussions" ? ` (${threads.length})` : ""}
                    {item.key === "members" ? ` (${active.member_count || 0})` : ""}
                    {item.key === "reports" ? ` (${active.report_count || 0})` : ""}
                  </button>
                ))}
              </div>

              {tab === "discussions" && (
                <>
                  <div className="flex justify-end mb-5">
                    {!creating && user && <button onClick={() => setCreating(true)} className="btn-primary" data-testid="new-thread-btn"><Plus className="w-4 h-4" /> Nouvelle discussion</button>}
                  </div>

                  {creating && (
                    <form onSubmit={createThread} className="card p-5 mb-5 space-y-3" data-testid="new-thread-form">
                      <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder="Titre de ta discussion" required data-testid="thread-title-input" />
                      <textarea value={body} onChange={(e) => setBody(e.target.value)} className="input" rows={4} placeholder="Décris ton sujet..." required data-testid="thread-body-input" />
                      <div className="flex gap-2">
                        <button type="submit" className="btn-primary" data-testid="thread-submit-btn"><Send className="w-4 h-4" /> Publier</button>
                        <button type="button" onClick={() => setCreating(false)} className="btn-ghost">Annuler</button>
                      </div>
                    </form>
                  )}

                  {threads.length === 0 ? (
                    <div className="card p-10 text-center text-gray-500">
                      <MessageCircle className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                      Aucune discussion ici. Sois le premier à en lancer une !
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {threads.map((t) => (
                        <div key={t.thread_id} className="card p-5 flex items-start gap-3 hover:bg-emerald-50/50" data-testid={`thread-item-${t.thread_id}`}>
                          <CommunityAvatar picture={t.user_picture} picturePosition={t.user_picture_position} name={t.user_name} />
                          <Link to={`/community/app/threads/${t.thread_id}`} className="flex-1 min-w-0">
                            <p className="font-heading font-bold">{t.title}</p>
                            <p className="text-sm text-gray-600 line-clamp-2">{t.body}</p>
                            <p className="text-xs text-gray-500 mt-1">{t.user_name} · {new Date(t.created_at).toLocaleDateString("fr-FR")} · {t.post_count} réponses</p>
                          </Link>
                          <div className="flex shrink-0 items-center gap-1">
                            {user && (
                              <button type="button" onClick={() => setReportTarget({ type: "thread", id: t.thread_id })} className="rounded-lg p-2 text-gray-400 hover:text-red-500 hover:bg-red-50" title="Signaler">
                                <Flag className="h-4 w-4" />
                              </button>
                            )}
                            {(isAdmin || t.user_id === user?.user_id) && (
                              <button
                                type="button"
                                onClick={() => deleteThread(t)}
                                className="rounded-lg p-2 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                title="Supprimer la discussion"
                                data-testid={`delete-thread-${t.thread_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {tab === "members" && (
                <div className="card overflow-hidden">
                  {!user ? (
                    <div className="p-8 text-center text-gray-500">Connecte-toi pour voir les membres.</div>
                  ) : members.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">Chargement des membres...</div>
                  ) : (
                    <div className="divide-y divide-emerald-100">
                      {members.map((m) => (
                        <div key={m.user_id} className="p-4 flex items-center gap-3">
                          <CommunityAvatar picture={m.picture} picturePosition={m.profile_picture_position} name={m.name} className="!w-10 !h-10" />
                          <div>
                            <p className="font-semibold">{m.name}</p>
                            {m.email && <p className="text-xs text-gray-500">{m.email}</p>}
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {m.user_id !== user?.user_id && (
                              <Link to={`/messages?to=${m.user_id}`} className="rounded-lg p-2 text-gray-500 hover:bg-emerald-50 hover:text-brand-green" title="Envoyer un message privé">
                                <MessageCircle className="h-4 w-4" />
                              </Link>
                            )}
                            <span className="text-xs rounded-full bg-emerald-50 px-3 py-1 text-brand-green">{m.role === "admin" ? "Admin" : "Membre"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === "about" && (
                <div className="card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="h-5 w-5 text-brand-green" />
                    <h3 className="font-heading text-2xl font-black">À propos</h3>
                  </div>
                  {active.about ? (
                    <RichTextRenderer html={active.about} className="text-gray-700 leading-relaxed" />
                  ) : (
                    <p className="text-gray-500">Aucune description pour cet espace.</p>
                  )}
                </div>
              )}

              {tab === "reports" && isAdmin && (
                <div className="card overflow-hidden">
                  {reports.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">Aucun signalement pour cet espace.</div>
                  ) : (
                    <div className="divide-y divide-emerald-100">
                      {reports.map((r) => (
                        <div key={r.report_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                          <div className="flex-1">
                            <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> {r.reason}</p>
                            <p className="text-sm text-gray-600">{r.details || "Aucun détail ajouté."}</p>
                            <p className="text-xs text-gray-500 mt-1">{r.reporter_name} · {new Date(r.created_at).toLocaleString("fr-FR")} · {r.target_type}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs rounded-full px-3 py-1 ${r.status === "resolved" ? "bg-emerald-50 text-brand-green" : "bg-amber-50 text-amber-700"}`}>{r.status}</span>
                            {r.status !== "resolved" && <button type="button" onClick={() => resolveReport(r.report_id)} className="btn-ghost !px-3 !py-2 text-sm">Marquer résolu</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {channelModal && (
        <Modal title={channelModal.mode === "edit" ? "Modifier l’espace" : "Créer un espace"} onClose={() => setChannelModal(null)}>
          <ChannelForm
            initial={channelModal.channel}
            saving={savingChannel}
            onCancel={() => setChannelModal(null)}
            onSubmit={saveChannel}
          />
        </Modal>
      )}

      {reportTarget && active && (
        <ReportModal
          target={reportTarget}
          channelId={active.channel_id}
          onClose={() => setReportTarget(null)}
          onDone={() => loadChannels(active.channel_id)}
        />
      )}
    </div>
  );
}

export function ThreadDetail() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [editingThread, setEditingThread] = useState(false);
  const [threadDraft, setThreadDraft] = useState({ title: "", body: "" });
  const [editingPostId, setEditingPostId] = useState(null);
  const [postDraft, setPostDraft] = useState("");

  const load = async () => {
    const r = await api.get(`/community/threads/${threadId}`);
    setData(r.data);
    setThreadDraft({ title: r.data.thread?.title || "", body: r.data.thread?.body || "" });
  };
  useEffect(() => { load(); }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    await api.post("/community/posts", { thread_id: threadId, body });
    setBody("");
    load();
  };

  const saveThreadEdit = async (e) => {
    e.preventDefault();
    await api.patch(`/community/threads/${threadId}`, threadDraft);
    setEditingThread(false);
    load();
  };

  const startPostEdit = (post) => {
    setEditingPostId(post.post_id);
    setPostDraft(post.body || "");
  };

  const savePostEdit = async (postId) => {
    await api.patch(`/community/posts/${postId}`, { body: postDraft });
    setEditingPostId(null);
    setPostDraft("");
    load();
  };

  const isAdmin = user?.role === "admin" || user?.role === "super-admin" || user?.role === "super_admin";
  const canEditThread = !!data?.thread && (isAdmin || data.thread.user_id === user?.user_id);
  const canDeleteThread = !!data?.thread && (isAdmin || data.thread.user_id === user?.user_id);

  const deleteCurrentThread = async () => {
    if (!data?.thread || !canDeleteThread) return;
    const ok = window.confirm(`Supprimer la discussion "${data.thread.title}" ?\n\nToutes les réponses et les signalements associés seront supprimés.`);
    if (!ok) return;
    await api.delete(`/community/threads/${data.thread.thread_id}`);
    navigate("/community/app");
  };

  if (!data) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="thread-detail-page">
      <Link to="/community/app" className="text-sm text-gray-500 inline-flex items-center gap-1 mb-4 hover:text-brand-green" data-testid="back-to-community"><ArrowLeft className="w-4 h-4" /> Retour</Link>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="font-heading text-3xl font-black">{data.thread.title}</h1>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canEditThread && (
            <button type="button" onClick={() => setEditingThread(true)} className="rounded-xl border border-emerald-100 px-3 py-2 text-sm text-brand-green hover:bg-emerald-50 inline-flex items-center gap-2">
              <Edit3 className="h-4 w-4" /> Modifier
            </button>
          )}
          {user && (
            <button type="button" onClick={() => setReportTarget({ type: "thread", id: data.thread.thread_id })} className="rounded-xl border border-red-100 px-3 py-2 text-sm text-red-500 hover:bg-red-50 inline-flex items-center gap-2">
              <Flag className="h-4 w-4" /> Signaler
            </button>
          )}
          {canDeleteThread && (
            <button
              type="button"
              onClick={deleteCurrentThread}
              className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-2"
              data-testid="delete-current-thread"
            >
              <Trash2 className="h-4 w-4" /> Supprimer
            </button>
          )}
        </div>
      </div>
      <div className="text-sm text-gray-500 mb-6 flex items-center gap-2">
        <CommunityAvatar picture={data.thread.user_picture} picturePosition={data.thread.user_picture_position} name={data.thread.user_name} className="!w-7 !h-7 text-[0.7rem]" />
        {data.thread.user_name} · {new Date(data.thread.created_at).toLocaleString("fr-FR")}
        {data.thread.edited_at && <span>· modifié</span>}
      </div>
      {editingThread ? (
        <form onSubmit={saveThreadEdit} className="card p-5 mb-6 space-y-3">
          <input className="input" value={threadDraft.title} onChange={(e) => setThreadDraft((prev) => ({ ...prev, title: e.target.value }))} required />
          <textarea className="input" rows={5} value={threadDraft.body} onChange={(e) => setThreadDraft((prev) => ({ ...prev, body: e.target.value }))} required />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditingThread(false)} className="btn-ghost">Annuler</button>
            <button type="submit" className="btn-primary !px-4 !py-2"><Check className="w-4 h-4" /> Enregistrer</button>
          </div>
        </form>
      ) : (
        <div className="card p-6 mb-6 whitespace-pre-line">{data.thread.body}</div>
      )}

      <h2 className="font-heading text-xl font-bold mb-3">{data.posts.length} réponse{data.posts.length > 1 ? "s" : ""}</h2>
      <div className="space-y-3 mb-8">
        {data.posts.map((p) => (
          <div key={p.post_id} className="card p-4" data-testid={`post-${p.post_id}`}>
            <div className="flex items-start gap-2 mb-2">
              <CommunityAvatar picture={p.user_picture} picturePosition={p.user_picture_position} name={p.user_name} className="!w-7 !h-7 text-[0.7rem]" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{p.user_name}</p>
                <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
              </div>
              {(isAdmin || p.user_id === user?.user_id) && (
                <button type="button" onClick={() => startPostEdit(p)} className="rounded-lg p-2 text-gray-400 hover:text-brand-green hover:bg-emerald-50" title="Modifier">
                  <Edit3 className="h-4 w-4" />
                </button>
              )}
              {user && (
                <button type="button" onClick={() => setReportTarget({ type: "post", id: p.post_id })} className="rounded-lg p-2 text-gray-400 hover:text-red-500 hover:bg-red-50" title="Signaler">
                  <Flag className="h-4 w-4" />
                </button>
              )}
            </div>
            {editingPostId === p.post_id ? (
              <div className="ml-9 space-y-2">
                <textarea className="input" rows={4} value={postDraft} onChange={(e) => setPostDraft(e.target.value)} />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setEditingPostId(null)} className="btn-ghost !px-3 !py-2 text-sm">Annuler</button>
                  <button type="button" onClick={() => savePostEdit(p.post_id)} className="btn-primary !px-3 !py-2 text-sm"><Check className="w-4 h-4" /> Enregistrer</button>
                </div>
              </div>
            ) : (
              <p className="text-sm whitespace-pre-line ml-9">{p.body}{p.edited_at && <span className="ml-2 text-xs text-gray-400">(modifié)</span>}</p>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="card p-4 space-y-2" data-testid="post-form">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} className="input" rows={3} placeholder="Ta réponse..." required data-testid="post-body-input" />
        <div className="text-right">
          <button type="submit" className="btn-primary !px-4 !py-2 text-sm" data-testid="post-submit-btn"><Send className="w-4 h-4" /> Répondre</button>
        </div>
      </form>

      {reportTarget && (
        <ReportModal
          target={reportTarget}
          channelId={data.thread.channel_id}
          onClose={() => setReportTarget(null)}
        />
      )}
    </div>
  );
}
